import { SmartToy } from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    ClickAwayListener,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { Ellipsis, Fullscreen, History, MessageCirclePlus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useChatAgent } from '../../Agents/hooks/useChatAgent';
import { MvpAgentChatDialog } from '../ChatAgentDialog/MvpAgentChatDialog';
import ChatHistoryPanel from '../MvpChatAgent-2/components/ChatHistoryPanel';
import ChatPanel from '../MvpChatAgent-2/components/ChatPanel';
import { renderFormattedText } from '../MvpChatAgent-2/components/TextFormatter';
import MvpChatCard from '../MvpChatAgent-2/MvpChatCard';

// Admin-spezifische Shortcuts
const adminShortcuts = [
    { text: 'Benutzer verwalten', action: 'Wie kann ich Benutzer hinzufügen oder bearbeiten?', highlighted: false },
    {
        text: 'System-Status',
        action: 'Zeige mir den aktuellen System-Status und Performance-Metriken',
        highlighted: true,
    },
    { text: 'Berechtigungen', action: 'Wie kann ich Benutzerberechtigungen und Rollen verwalten?', highlighted: false },
    { text: 'Fehler-Diagnose', action: 'Hilf mir bei der Diagnose von System-Fehlern und Logs', highlighted: false },
    { text: 'Backup & Wartung', action: 'Wie führe ich Backups durch und plane Wartungsarbeiten?', highlighted: false },
    {
        text: 'Sicherheit',
        action: 'Zeige mir Sicherheitseinstellungen und potenzielle Bedrohungen',
        highlighted: false,
    },
];

const highlightedShortcut = adminShortcuts[0];
const uiTexts = {
    chatTitle: 'T4LO Admin Assistent',
    chatSubtitle: 'System-Management & Support',
};

export default function AdminCompactChatAgent() {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).chatAgent;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        chat,
        messages,
        isTyping,
        sendMessage: sendChatMessage,
        resetChat,
        getChatList,
        loadChat,
    } = useChatAgent({
        dummyMessage: 'Hallo! Ich bin dein Admin-Assistent. Wie kann ich dir helfen?',
    });
    const chatList = useSelector((state: AppState) => state.chatAgent.chatList);
    const [input, setInput] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
    const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(null);
    const historyOpen = Boolean(historyAnchorEl);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleShortcutAction = (action: string) => {
        setInput(action);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleHistoryClick = (event: React.MouseEvent<HTMLElement>) => {
        setHistoryAnchorEl(historyAnchorEl ? null : event.currentTarget);
        if (!historyAnchorEl) {
            getChatList();
        }
    };

    const handleHistoryClose = () => {
        setHistoryAnchorEl(null);
    };

    const clearChat = () => {
        resetChat();
        handleMenuClose();
    };

    const handleNewChat = () => {
        resetChat();
        setInput('');
    };

    const sendMessage = async () => {
        const trimmedInput = input.trim();
        if (!trimmedInput) return;

        const success = await sendChatMessage(trimmedInput);
        if (success) {
            setInput('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }

        if (!isTyping && inputRef.current && messages.length > 0) {
            setTimeout(() => {
                inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
        }
    }, [messages, isTyping]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const menuButton = (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
                color="inherit"
                onClick={handleNewChat}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title={langStrings.newChat}
            >
                <MessageCirclePlus size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={handleHistoryClick}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Chat-Verlauf"
            >
                <History size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={() => setFullscreenDialogOpen(true)}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Fullscreen"
            >
                <Fullscreen size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Menu"
            >
                <Ellipsis size={isMobile ? 16 : 20} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={clearChat}>{langStrings.deleteChat}</MenuItem>
            </Menu>
        </Box>
    );

    return (
        <MvpChatCard
            highlightLeftBar={false}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            height: isMobile ? '3rem' : '3rem',
                            width: isMobile ? '3rem' : '3rem',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: `0 0 0.75rem ${alpha(theme.palette.primary.main, 0.7)}`,
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        <SmartToy
                            sx={{ fontSize: isMobile ? '1.5rem' : '2rem', color: theme.palette.primary.contrastText }}
                        />
                    </Avatar>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1,
                                textAlign: 'left',
                                ml: 2,
                                color: theme.palette.text.primary,
                                fontFamily: '"Bricolage Grotesque", sans-serif',
                                letterSpacing: '-0.05em',
                            }}
                        >
                            {uiTexts.chatTitle}
                        </Typography>
                        {!isMobile && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 300,
                                    ml: 2,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.5,
                                    textAlign: 'left',
                                    color: 'text.secondary',
                                }}
                            >
                                {uiTexts.chatSubtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            }
            rightButtons={menuButton}
            style={{
                height: 'auto',
                minHeight: isMobile ? 'auto' : '300px',
                maxHeight: 'none',
                width: '100%',
                maxWidth: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease-in-out',
                padding: isMobile ? '16px' : '24px',
                boxShadow: theme.shadows[4],
            }}
        >
            <Box
                sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%' }}
            >
                <ChatPanel
                    messages={messages}
                    input={input}
                    setInput={setInput}
                    sendMessage={sendMessage}
                    handleKeyPress={handleKeyPress}
                    isTyping={isTyping}
                    chat={chat}
                    scrollRef={scrollRef}
                    inputRef={inputRef}
                    renderFormattedText={renderFormattedText}
                    langStrings={langStrings}
                    shortcuts={adminShortcuts}
                    highlightedShortcut={highlightedShortcut}
                    handleShortcutAction={handleShortcutAction}
                    isMobile={isMobile}
                    isAgentAction={false}
                />
            </Box>

            {/* History Popper */}
            <Popper open={historyOpen} anchorEl={historyAnchorEl} placement="bottom-end" sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={handleHistoryClose}>
                    <Paper
                        elevation={8}
                        sx={{
                            width: 320,
                            maxHeight: 400,
                            overflow: 'auto',
                            mt: 1,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: alpha(theme.palette.background.paper, 0.95),
                            backdropFilter: 'blur(10px)',
                            p: 0,
                            pt: 1,
                        }}
                    >
                        <ChatHistoryPanel
                            chatThreads={chatList.map((chat) => ({
                                id: chat.chatId,
                                title: chat.title,
                                lastMessage: '',
                                timestamp: `${new Date(chat.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date(chat.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
                                unread: false,
                            }))}
                            historyExpanded={true}
                            onToggleHistory={() => {}}
                            onLoadChat={(chatId) => {
                                loadChat(chatId);
                                handleHistoryClose();
                            }}
                            onRefreshChats={getChatList}
                            currentChatId={chat?.chatId}
                            langStrings={langStrings}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>

            <MvpAgentChatDialog open={fullscreenDialogOpen} onClose={() => setFullscreenDialogOpen(false)} />
        </MvpChatCard>
    );
}
